import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import api from '../api';
import InvitationBuilder from '../components/InvitationBuilder';
import useServerDraft from '../hooks/useServerDraft';

function getWorkerInfo() {
  try {
    const token = localStorage.getItem('token');
    if (!token) return { role: 'project_manager', name: '' };
    const p = JSON.parse(decodeURIComponent(escape(atob(token.split('.')[1]))));
    return { role: p.role || 'project_manager', name: `${p.workerFirstName || ''} ${p.workerLastName || ''}`.trim(), userName: p.userName || '' };
  } catch { return { role: 'project_manager', name: '', userName: '' }; }
}

// event_date is a naive "YYYY-MM-DD HH:mm:ss" wall-clock string (no
// timezone) — parsing it with `new Date(...)` and letting toLocaleString
// apply the browser's own timezone is unreliable across browsers, so format
// it directly from its components instead.
function formatEventDateShort(raw) {
  const m = String(raw || '').match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
  if (!m) return '';
  const [, y, mo, d, h, mi] = m;
  return `${parseInt(d, 10)}.${parseInt(mo, 10)}.${y}, ${h}:${mi}`;
}

const RSVP_STATUS_INFO = {
  attending: { label: 'מגיע', bg: '#dcfce7', text: '#15803d', dot: '#22c55e' },
  not_attending: { label: 'לא מגיע', bg: '#fee2e2', text: '#b91c1c', dot: '#ef4444' },
  unknown: { label: 'לא ידוע', bg: '#fef9c3', text: '#a16207', dot: '#eab308' },
};

function attendancePercent(summary) {
  const total = summary.attending + summary.not_attending + summary.unknown;
  return total ? Math.round((summary.attending / total) * 100) : 0;
}

// red under 40%, yellow 40-60%, green above 60%
function percentColor(pct) {
  if (pct < 40) return { ring: '#ef4444', bg: '#fee2e2', text: '#b91c1c' };
  if (pct <= 60) return { ring: '#eab308', bg: '#fef9c3', text: '#a16207' };
  return { ring: '#22c55e', bg: '#dcfce7', text: '#15803d' };
}

function PercentCircle({ summary }) {
  const pct = attendancePercent(summary);
  const c = percentColor(pct);
  return (
    <div title="אחוז שאישרו הגעה" style={{ width: 54, height: 54, borderRadius: '50%', border: `4px solid ${c.ring}`, background: c.bg, color: c.text, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '0.85rem', flexShrink: 0 }}>
      {pct}%
    </div>
  );
}

export default function Events() {
  const { projectName } = useParams();
  const navigate = useNavigate();
  const name = decodeURIComponent(projectName);
  const { name: workerName } = getWorkerInfo();
  const [events, setEvents] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const EMPTY_FORM = { title: '', description: '', eventDate: '', eventHour: '12', eventMinute: '00', buildingKeys: [], pdfUrl: '' };
  const enc = encodeURIComponent(name);
  const [form, setForm] = useServerDraft(enc, 'event', EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [openEvent, setOpenEvent] = useState(null); // event object
  const [rsvps, setRsvps] = useState([]);
  const [busy, setBusy] = useState(false);
  const [reminderModalEvent, setReminderModalEvent] = useState(null);
  const [reminderDraft, setReminderDraft] = useState(null); // { enabled, time, rules: [{start_date, interval_days}] }
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [showBuilder, setShowBuilder] = useState(false);
  const [view, setView] = useState(null); // null | 'create' | 'responses'

  const loadEvents = useCallback(() => {
    api.get(`/portal/events/${enc}`).then(r => setEvents(r.data || [])).catch(() => {});
  }, [enc]);

  useEffect(() => {
    loadEvents();
    api.get(`/portal/buildings-structured/${enc}`).then(r => setBuildings(r.data || [])).catch(() => {});
    api.get(`/portal/event-templates/${enc}`).then(r => setTemplates(r.data || [])).catch(() => {});
  }, [enc, loadEvents]);

  async function uploadPdf(file) {
    setUploadingPdf(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post(`/portal/events/${enc}/upload-pdf`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(p => ({ ...p, pdfUrl: res.data.url }));
    } catch { alert('שגיאה בהעלאת הקובץ'); }
    setUploadingPdf(false);
  }

  function applyTemplate(id) {
    setSelectedTemplateId(id);
    const tpl = templates.find(t => String(t.id) === String(id));
    if (tpl) setForm(p => ({ ...p, title: tpl.title || '', description: tpl.description || '' }));
  }

  async function saveAsTemplate() {
    const tplName = window.prompt('שם לתבנית:');
    if (!tplName || !tplName.trim()) return;
    setSavingTemplate(true);
    try {
      const res = await api.post(`/portal/event-templates/${enc}`, { name: tplName, title: form.title, description: form.description });
      setTemplates(p => [res.data, ...p]);
    } catch { alert('שגיאה בשמירת התבנית'); }
    setSavingTemplate(false);
  }

  async function deleteTemplate(id) {
    if (!window.confirm('למחוק את התבנית?')) return;
    await api.delete(`/portal/event-templates/${id}`);
    setTemplates(p => p.filter(t => t.id !== id));
    if (String(selectedTemplateId) === String(id)) setSelectedTemplateId('');
  }

  function toggleBuildingKey(key) {
    setForm(p => ({
      ...p,
      buildingKeys: p.buildingKeys.includes(key) ? p.buildingKeys.filter(k => k !== key) : [...p.buildingKeys, key],
    }));
  }

  function buildingLabel(key) {
    const [addr, num] = key.split('|||');
    return `${addr} ${num}`;
  }

  // Builds a "YYYY-MM-DDTHH:mm" string (24-hour) from the date + hour + minute fields.
  function buildEventDateString() {
    if (!form.eventDate) return null;
    return `${form.eventDate}T${String(form.eventHour).padStart(2, '0')}:${form.eventMinute}`;
  }

  async function createEvent() {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      const res = await api.post(`/portal/events/${enc}`, {
        title: form.title, description: form.description, event_date: buildEventDateString(),
        building_keys: form.buildingKeys, created_by: workerName, pdf_url: form.pdfUrl || null,
      });
      setEvents(p => [res.data, ...p]);
      setForm(EMPTY_FORM);
      setSelectedTemplateId('');
    } catch {}
    setSaving(false);
  }

  function openReminderModal(event) {
    let rules = [];
    try { rules = event.reminder_rules ? JSON.parse(event.reminder_rules) : []; } catch { rules = []; }
    if (!rules.length) rules = [{ start_date: '', interval_days: 7 }];
    setReminderDraft({ enabled: !!event.reminder_enabled, time: event.reminder_time || '10:00', rules });
    setReminderModalEvent(event);
  }

  function updateReminderRule(i, field, value) {
    setReminderDraft(p => ({ ...p, rules: p.rules.map((r, idx) => idx === i ? { ...r, [field]: value } : r) }));
  }

  function addReminderRule() {
    setReminderDraft(p => ({ ...p, rules: [...p.rules, { start_date: '', interval_days: 2 }] }));
  }

  function removeReminderRule(i) {
    setReminderDraft(p => ({ ...p, rules: p.rules.filter((_, idx) => idx !== i) }));
  }

  async function saveReminderSchedule() {
    setBusy(true);
    try {
      const rules = reminderDraft.rules.filter(r => r.start_date && r.interval_days);
      const res = await api.patch(`/portal/events/${reminderModalEvent.id}/reminder-schedule`, {
        reminder_enabled: reminderDraft.enabled, reminder_rules: rules, reminder_time: reminderDraft.time,
      });
      setEvents(p => p.map(e => e.id === res.data.id ? { ...e, ...res.data } : e));
      setReminderModalEvent(null);
    } catch { alert('שגיאה בשמירת התזכורת'); }
    setBusy(false);
  }

  async function deleteEvent(id) {
    if (!window.confirm('למחוק את הכנס וכל אישורי ההגעה שלו?')) return;
    await api.delete(`/portal/events/${id}`);
    setEvents(p => p.filter(e => e.id !== id));
    if (openEvent?.id === id) setOpenEvent(null);
  }

  function eventBuildingKeys(event) {
    try { return event.building_keys ? JSON.parse(event.building_keys) : []; } catch { return []; }
  }

  async function sendInvites(event) {
    const keys = eventBuildingKeys(event);
    const where = keys.length ? `בבניינים: ${keys.map(buildingLabel).join(', ')}` : 'בכל הפרויקט';
    if (!window.confirm(`לשלוח SMS הזמנה לכל הדיירים ${where}?`)) return;
    setBusy(true);
    try {
      const res = await api.post(`/portal/events/${event.id}/send`, {});
      alert(`נשלחו ${res.data.sent} מתוך ${res.data.total} הודעות`);
      loadEvents();
      if (openEvent?.id === event.id) openEventDetail(event);
    } catch { alert('שגיאה בשליחה'); }
    setBusy(false);
  }

  async function sendReminder(event) {
    if (!window.confirm('לשלוח תזכורת רק למי שעדיין לא ענה?')) return;
    setBusy(true);
    try {
      const res = await api.post(`/portal/events/${event.id}/remind`, {});
      alert(`נשלחה תזכורת ל-${res.data.sent} דיירים`);
    } catch { alert('שגיאה בשליחה'); }
    setBusy(false);
  }

  async function openEventDetail(event) {
    setOpenEvent(event);
    const res = await api.get(`/portal/events/${event.id}/rsvps`).catch(() => ({ data: [] }));
    setRsvps(res.data || []);
  }

  async function exportExcel(event) {
    try {
      const res = await api.get(`/portal/events/${event.id}/export`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${event.title || 'event'}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch { alert('שגיאה בייצוא לאקסל'); }
  }

  const card = { background: 'white', borderRadius: 14, padding: 20, border: '1px solid #e5e7eb', marginBottom: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' };
  const inp = { width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '0.9rem', boxSizing: 'border-box', fontFamily: 'inherit', direction: 'rtl' };

  function openTenant(r) {
    if (!r.tenant_id) return;
    navigate(`/buildings/${projectName}?editTenant=${r.tenant_id}&addr=${encodeURIComponent(r.building_address || '')}&num=${encodeURIComponent(r.building_number || '')}`);
  }

  function renderRsvpTable() {
    return (
      <div style={{ marginTop: 16, borderTop: '1px solid #e5e7eb', paddingTop: 14, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
          <thead>
            <tr style={{ background: '#f9fafb', textAlign: 'right' }}>
              <th style={{ padding: '6px 10px' }}>שם</th>
              <th style={{ padding: '6px 10px' }}>טלפון</th>
              <th style={{ padding: '6px 10px' }}>כתובת</th>
              <th style={{ padding: '6px 10px' }}>דירה</th>
              <th style={{ padding: '6px 10px' }}>סטטוס</th>
            </tr>
          </thead>
          <tbody>
            {rsvps.map(r => {
              const info = RSVP_STATUS_INFO[r.status] || RSVP_STATUS_INFO.unknown;
              return (
                <tr key={r.id} style={{ borderTop: '1px solid #f3f4f6', cursor: r.tenant_id ? 'pointer' : 'default' }} onClick={() => openTenant(r)}>
                  <td style={{ padding: '6px 10px', color: r.tenant_id ? '#1d4ed8' : 'inherit', fontWeight: 600 }}>{r.name}</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right' }}><bdi>{r.phone}</bdi></td>
                  <td style={{ padding: '6px 10px' }}>{r.building_address} {r.building_number}</td>
                  <td style={{ padding: '6px 10px' }}>{r.apartment_number}</td>
                  <td style={{ padding: '6px 10px' }}>
                    <span style={{ background: info.bg, color: info.text, borderRadius: 99, padding: '2px 12px', fontSize: '0.78rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: info.dot, display: 'inline-block' }} /> {info.label}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  const tileStyle = active => ({ flex: '1 1 200px', maxWidth: 260, background: 'white', border: active ? '2px solid #1B2A4A' : '1px solid #e5e7eb', borderRadius: 14, padding: '22px 12px', textAlign: 'center', cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', fontFamily: 'inherit', fontWeight: 700, fontSize: '1rem', color: '#1B2A4A' });

  return (
    <div>
      <Navbar title={`כנסים — ${name}`} breadcrumb="פרויקטים > כנסים" />
      <div className="container" style={{ direction: 'rtl' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 className="page-title">🎉 כנסים</h2>
          <button className="btn btn-secondary" onClick={() => navigate(`/project-hub/${projectName}`)}>← חזרה</button>
        </div>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 20 }}>
          <button onClick={() => setView(v => (v === 'create' ? null : 'create'))} style={tileStyle(view === 'create')}>
            <div style={{ fontSize: '2rem', marginBottom: 6 }}>🎉</div>יצירת כנס
          </button>
          <button onClick={() => setView(v => (v === 'responses' ? null : 'responses'))} style={tileStyle(view === 'responses')}>
            <div style={{ fontSize: '2rem', marginBottom: 6 }}>📋</div>צפייה בתשובות
          </button>
        </div>

        {view === 'responses' && (
          <>
            {events.length === 0 && <div style={{ color: '#9ca3af', fontSize: '0.9rem' }}>אין כנסים עדיין</div>}
            {events.map(ev => (
              <div key={ev.id} style={card}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                  <PercentCircle summary={ev.summary} />
                  <div style={{ flex: 1, minWidth: 180 }}>
                    <div style={{ fontWeight: 700, color: '#1f2937' }}>{ev.title}</div>
                    <div style={{ fontSize: '0.82rem', color: '#6b7280', marginTop: 2 }}>
                      {ev.event_date && formatEventDateShort(ev.event_date)}
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                      <span style={{ background: RSVP_STATUS_INFO.attending.bg, color: RSVP_STATUS_INFO.attending.text, borderRadius: 99, padding: '2px 10px', fontSize: '0.78rem', fontWeight: 600 }}>🟢 {ev.summary.attending}</span>
                      <span style={{ background: RSVP_STATUS_INFO.not_attending.bg, color: RSVP_STATUS_INFO.not_attending.text, borderRadius: 99, padding: '2px 10px', fontSize: '0.78rem', fontWeight: 600 }}>🔴 {ev.summary.not_attending}</span>
                      <span style={{ background: RSVP_STATUS_INFO.unknown.bg, color: RSVP_STATUS_INFO.unknown.text, borderRadius: 99, padding: '2px 10px', fontSize: '0.78rem', fontWeight: 600 }}>🟡 {ev.summary.unknown}</span>
                    </div>
                  </div>
                  <button onClick={() => (openEvent?.id === ev.id ? setOpenEvent(null) : openEventDetail(ev))} style={{ background: '#f3f4f6', color: '#374151', border: 'none', padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}>
                    {openEvent?.id === ev.id ? 'הסתר תשובות' : 'צפה בתשובות'}
                  </button>
                  <button onClick={() => exportExcel(ev)} style={{ background: '#15803d', color: 'white', border: 'none', padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}>📊 ייצוא לאקסל</button>
                </div>
                {openEvent?.id === ev.id && renderRsvpTable()}
              </div>
            ))}
          </>
        )}

        {view === 'create' && (<>
        <div style={card}>
          <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: 14, color: '#1B2A4A' }}>➕ יצירת כנס חדש</div>
          {templates.length > 0 && (
            <div style={{ marginBottom: 14, display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: '0.8rem', color: '#6b7280', display: 'block', marginBottom: 4 }}>התחל מתבנית</label>
                <select style={inp} value={selectedTemplateId} onChange={e => applyTemplate(e.target.value)}>
                  <option value="">— ללא תבנית —</option>
                  {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              {selectedTemplateId && (
                <button onClick={() => deleteTemplate(selectedTemplateId)} style={{ background: 'none', border: '1px solid #fecaca', color: '#dc2626', borderRadius: 8, padding: '8px 12px', cursor: 'pointer', fontSize: '0.8rem' }}>🗑 מחק תבנית</button>
              )}
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div style={{ gridColumn: '1/-1' }}>
              <label style={{ fontSize: '0.8rem', color: '#6b7280', display: 'block', marginBottom: 4 }}>כותרת הכנס *</label>
              <input style={inp} value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="לדוגמה: כנס דיירים בניין 5" />
            </div>
            <div>
              <label style={{ fontSize: '0.8rem', color: '#6b7280', display: 'block', marginBottom: 4 }}>תאריך ושעה</label>
              <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end' }}>
                <input type="date" style={{ ...inp, flex: '2 1 0' }} value={form.eventDate} onChange={e => setForm(p => ({ ...p, eventDate: e.target.value }))} />
                <div style={{ flex: '1 1 0' }}>
                  <label style={{ fontSize: '0.7rem', color: '#9ca3af', display: 'block' }}>שעה (24)</label>
                  <select style={inp} value={form.eventHour} onChange={e => setForm(p => ({ ...p, eventHour: e.target.value }))}>
                    {Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0')).map(h => <option key={h} value={h}>{h}</option>)}
                  </select>
                </div>
                <div style={{ flex: '1 1 0' }}>
                  <label style={{ fontSize: '0.7rem', color: '#9ca3af', display: 'block' }}>דקות</label>
                  <select style={inp} value={form.eventMinute} onChange={e => setForm(p => ({ ...p, eventMinute: e.target.value }))}>
                    {Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0')).map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
              </div>
            </div>
            <div>
              <label style={{ fontSize: '0.8rem', color: '#6b7280', display: 'block', marginBottom: 4 }}>
                בניינים {form.buildingKeys.length === 0 && <span style={{ color: '#9ca3af' }}>(ריק = כל הפרויקט)</span>}
              </label>
              <div style={{ border: '1px solid #d1d5db', borderRadius: 8, padding: 8, maxHeight: 140, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
                {buildings.map(b => {
                  const key = `${b.buildingAddress}|||${b.buildingNumber}`;
                  return (
                    <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: '0.88rem' }}>
                      <input type="checkbox" checked={form.buildingKeys.includes(key)} onChange={() => toggleBuildingKey(key)} />
                      {b.buildingAddress} {b.buildingNumber}
                    </label>
                  );
                })}
              </div>
            </div>
            <div style={{ gridColumn: '1/-1' }}>
              <label style={{ fontSize: '0.8rem', color: '#6b7280', display: 'block', marginBottom: 4 }}>תיאור (לא חובה)</label>
              <textarea style={{ ...inp, resize: 'vertical', minHeight: 50 }} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
            </div>
            <div style={{ gridColumn: '1/-1' }}>
              <label style={{ fontSize: '0.8rem', color: '#6b7280', display: 'block', marginBottom: 4 }}>📄 מודעת כנס (PDF) — יצורף קישור אליו בהזמנה</label>
              <input type="file" accept="application/pdf" onChange={e => e.target.files[0] && uploadPdf(e.target.files[0])} disabled={uploadingPdf} />
              {uploadingPdf && <span style={{ fontSize: '0.8rem', color: '#6b7280', marginRight: 8 }}>מעלה...</span>}
              {form.pdfUrl && !uploadingPdf && (
                <div style={{ marginTop: 6, fontSize: '0.82rem' }}>
                  <a href={form.pdfUrl} target="_blank" rel="noreferrer" style={{ color: '#1d4ed8' }}>✅ הקובץ הועלה — צפייה</a>
                  <button onClick={() => setForm(p => ({ ...p, pdfUrl: '' }))} style={{ marginRight: 10, background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '0.82rem' }}>הסר</button>
                </div>
              )}
              <div style={{ marginTop: 10 }}>
                <button type="button" onClick={() => setShowBuilder(v => !v)} style={{ background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 8, padding: '6px 14px', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}>
                  {showBuilder ? '✖ סגור' : (form.pdfUrl ? '✏️ ערוך את המודעה' : '🧩 צור מודעה מתבנית')}
                </button>
              </div>
              {(
                <div style={{ marginTop: 12, border: '1px solid #e5e7eb', borderRadius: 10, padding: 14, display: showBuilder ? 'block' : 'none' }}>
                  <InvitationBuilder projectName={name} enc={enc} eventDate={form.eventDate} eventHour={form.eventHour} eventMinute={form.eventMinute}
                    onUploaded={url => { setForm(p => ({ ...p, pdfUrl: url })); setShowBuilder(false); }} />
                </div>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            <button onClick={createEvent} disabled={saving || !form.title.trim()} style={{ background: '#1B2A4A', color: 'white', border: 'none', padding: '9px 24px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, opacity: (!form.title.trim() || saving) ? 0.6 : 1 }}>
              {saving ? 'יוצר...' : 'צור כנס'}
            </button>
            <button onClick={saveAsTemplate} disabled={savingTemplate || (!form.title.trim() && !form.description.trim())} style={{ background: '#f3f4f6', color: '#374151', border: 'none', padding: '9px 18px', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>
              💾 שמור כתבנית
            </button>
          </div>
        </div>

        <div style={{ fontWeight: 700, fontSize: '1rem', color: '#1B2A4A', marginBottom: 10 }}>📜 היסטוריית כנסים</div>
        {events.length === 0 && <div style={{ color: '#9ca3af', fontSize: '0.9rem' }}>אין כנסים עדיין</div>}
        {events.map(ev => (
          <div key={ev.id} style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
              <PercentCircle summary={ev.summary} />
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ fontWeight: 700, color: '#1f2937' }}>{ev.title}</div>
                <div style={{ fontSize: '0.82rem', color: '#6b7280', marginTop: 2 }}>
                  {eventBuildingKeys(ev).length ? `בניינים: ${eventBuildingKeys(ev).map(buildingLabel).join(', ')}` : 'כל הפרויקט'}
                  {ev.event_date && ` · ${formatEventDateShort(ev.event_date)}`}
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                  <span style={{ background: RSVP_STATUS_INFO.attending.bg, color: RSVP_STATUS_INFO.attending.text, borderRadius: 99, padding: '2px 10px', fontSize: '0.78rem', fontWeight: 600 }}>🟢 {ev.summary.attending}</span>
                  <span style={{ background: RSVP_STATUS_INFO.not_attending.bg, color: RSVP_STATUS_INFO.not_attending.text, borderRadius: 99, padding: '2px 10px', fontSize: '0.78rem', fontWeight: 600 }}>🔴 {ev.summary.not_attending}</span>
                  <span style={{ background: RSVP_STATUS_INFO.unknown.bg, color: RSVP_STATUS_INFO.unknown.text, borderRadius: 99, padding: '2px 10px', fontSize: '0.78rem', fontWeight: 600 }}>🟡 {ev.summary.unknown}</span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {!ev.invites_sent_at && (
                  <button onClick={() => sendInvites(ev)} disabled={busy} style={{ background: '#1d4ed8', color: 'white', border: 'none', padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}>📤 שלח הזמנות</button>
                )}
                {!!ev.invites_sent_at && (
                  <>
                    <button onClick={() => (openEvent?.id === ev.id ? setOpenEvent(null) : openEventDetail(ev))} style={{ background: '#f3f4f6', color: '#374151', border: 'none', padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}>{openEvent?.id === ev.id ? 'הסתר תשובות' : '👁 צפה בתשובות'}</button>
                    <button onClick={() => sendReminder(ev)} disabled={busy} style={{ background: '#f3f4f6', color: '#374151', border: 'none', padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}>⏰ שלח תזכורת עכשיו</button>
                    <button onClick={() => openReminderModal(ev)} style={{ background: ev.reminder_enabled ? '#15803d' : '#eab308', color: 'white', border: 'none', padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}>
                      {ev.reminder_enabled ? '✅ תזכורת אוטומטית פעילה' : '⚙️ הגדר תזכורת אוטומטית'}
                    </button>
                    <button onClick={() => exportExcel(ev)} style={{ background: '#15803d', color: 'white', border: 'none', padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}>📊 ייצוא לאקסל</button>
                  </>
                )}
                <button onClick={() => deleteEvent(ev.id)} style={{ background: 'none', color: '#dc2626', border: '1px solid #fecaca', padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}>🗑 מחק</button>
              </div>
            </div>

            {openEvent?.id === ev.id && renderRsvpTable()}
          </div>
        ))}
        </>)}
      </div>

      {reminderModalEvent && reminderDraft && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setReminderModalEvent(null)}>
          <div style={{ background: 'white', borderRadius: 14, padding: 22, width: '100%', maxWidth: 480, direction: 'rtl', maxHeight: '85vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ fontWeight: 700, fontSize: '1.05rem', marginBottom: 4, color: '#1B2A4A' }}>⚙️ תזכורת אוטומטית — {reminderModalEvent.title}</div>
            <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: 14 }}>נשלחת רק למי שעדיין לא ענה ("לא יודע"). מי שענה — לא מקבל, ומי שעונה אחר כך מפסיק לקבל.</div>

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem', marginBottom: 14 }}>
              <input type="checkbox" checked={reminderDraft.enabled} onChange={e => setReminderDraft(p => ({ ...p, enabled: e.target.checked }))} />
              הפעל תזכורת אוטומטית
            </label>

            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: '0.8rem', color: '#6b7280', display: 'block', marginBottom: 4 }}>שעת שליחה (24 שעות)</label>
              <input type="time" style={inp} value={reminderDraft.time} onChange={e => setReminderDraft(p => ({ ...p, time: e.target.value }))} />
            </div>

            <div style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: 6 }}>שלבים (ככל שמתקרבים לכנס אפשר להגביר תדירות)</div>
            {reminderDraft.rules.map((r, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 8 }}>
                <div style={{ flex: 2 }}>
                  <label style={{ fontSize: '0.7rem', color: '#9ca3af', display: 'block' }}>החל מתאריך</label>
                  <input type="date" style={inp} value={r.start_date} onChange={e => updateReminderRule(i, 'start_date', e.target.value)} />
                </div>
                <div style={{ flex: 2 }}>
                  <label style={{ fontSize: '0.7rem', color: '#9ca3af', display: 'block' }}>כל כמה ימים</label>
                  <select style={inp} value={r.interval_days} onChange={e => updateReminderRule(i, 'interval_days', parseInt(e.target.value, 10))}>
                    {[1, 2, 3, 4, 5, 6, 7, 14].map(d => <option key={d} value={d}>{d === 1 ? 'כל יום' : `כל ${d} ימים`}</option>)}
                  </select>
                </div>
                {reminderDraft.rules.length > 1 && (
                  <button onClick={() => removeReminderRule(i)} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '1.1rem', padding: '8px' }}>🗑</button>
                )}
              </div>
            ))}
            <button onClick={addReminderRule} style={{ background: 'none', border: '1px dashed #9ca3af', color: '#6b7280', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: '0.82rem', marginBottom: 18 }}>+ הוסף שלב (למשל: החמרה לקראת הכנס)</button>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setReminderModalEvent(null)} style={{ background: '#f3f4f6', color: '#374151', border: 'none', padding: '8px 18px', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>ביטול</button>
              <button onClick={saveReminderSchedule} disabled={busy} style={{ background: '#1B2A4A', color: 'white', border: 'none', padding: '8px 18px', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>{busy ? 'שומר...' : 'שמור'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
